/**
 * Prijsblok — bijwerken bij een variantwissel. Hoort bij blocks/zorenza-price.liquid.
 *
 * Horizon vervangt bij een variantwissel wel zijn eigen prijsonderdeel, maar
 * niet dit blok: het blijft hetzelfde element staan. Zonder dit script bleef
 * de prijs dus hangen op de variant die bij het laden van de pagina actief
 * was — en dan leest de klant een ander bedrag dan hij afrekent. Van 5er naar
 * 10er was dat een te lage prijs, en daar gaat de PAngV over.
 *
 * De som is dezelfde als in het blok:
 *   korting% = round((vanaf − prijs) × 100 / vanaf)
 * Wijzig je hier iets, wijzig het daar dan ook.
 */

const TAG = 'zorenza-price';

class ZorenzaPrice extends HTMLElement {
  /** prijs en vanaf-prijs in centen, per variant-id. */
  #varianten = new Map();

  /** Variant waarvoor wat er nu staat klopt. */
  #nu = '';

  connectedCallback() {
    this.#varianten = new Map(
      (this.dataset.varianten || '')
        .split(',')
        .filter(Boolean)
        .map((rij) => {
          const [id, prijs, vanaf] = rij.split(':');
          return [id, { prijs: Number(prijs) || 0, vanaf: Number(vanaf) || 0 }];
        })
    );
    this.#nu = this.dataset.variantNu || '';
    document.addEventListener('change', this.#onWissel);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.#onWissel);
  }

  /**
   * De variantkiezer zet het verborgen id-veld niet in hetzelfde tikje als
   * waarin het change-event valt, dus even doorkijken tot het nummer verandert.
   */
  #onWissel = (event) => {
    if (this.contains(event.target)) return;

    let pogingen = 0;
    const kijk = () => {
      const id = document.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
      if (id && id !== this.#nu) {
        this.#nu = id;
        this.#teken(this.#varianten.get(id));
        return;
      }
      if (++pogingen < 20) setTimeout(kijk, 100);
    };
    kijk();
  };

  #teken(v) {
    if (!v || v.prijs <= 0) return;

    const nu = this.querySelector('.z-price__now');
    const was = this.querySelector('.z-price__was');
    const wasBedrag = was?.querySelector('s');
    const pct = this.querySelector('.z-price__pct');

    // innerHTML en niet textContent: shop.money_format is in deze winkel door
    // een valuta-app gevuld met een <span class=cvc-money>…</span>. Als tekst
    // zou de klant die tags letterlijk zien staan; als HTML blijft de app zijn
    // haak houden om bedragen om te rekenen.
    if (nu) nu.innerHTML = this.#geld(v.prijs);

    const afgeprijsd = v.vanaf > v.prijs;

    if (was) {
      was.hidden = !afgeprijsd;
      if (afgeprijsd && wasBedrag) wasBedrag.innerHTML = this.#geld(v.vanaf);
    }

    if (pct) {
      const procent = afgeprijsd ? Math.round(((v.vanaf - v.prijs) * 100) / v.vanaf) : 0;
      pct.hidden = !afgeprijsd || procent <= 0;

      if (!pct.hidden) {
        // De naam van de actie staat in een eigen span vóór het getal. Met
        // textContent zou die bij de eerste variantwissel verdwijnen, dus
        // alleen het getal aanraken en de naam laten staan.
        const naam = pct.querySelector('.z-price__pct-naam');
        if (naam) {
          naam.nextSibling ? (naam.nextSibling.textContent = ` −${procent} %`) : pct.append(` −${procent} %`);
        } else {
          pct.textContent = `−${procent} %`;
        }
      }
    }
  }

  /** Centen naar de opmaak van de winkel, via shop.money_format. */
  #geld(centen) {
    const patroon = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
    const bedrag = centen / 100;

    const knip = (decimalen, decimaalteken, duizendteken) => {
      const vast = bedrag.toFixed(decimalen);
      const [heel, deel] = vast.split('.');
      const metDuizend = heel.replace(/\B(?=(\d{3})+(?!\d))/g, duizendteken);
      return decimalen > 0 ? metDuizend + decimaalteken + deel : metDuizend;
    };

    return patroon
      .replace('{{amount_no_decimals_with_comma_separator}}', knip(0, '', '.'))
      .replace('{{amount_with_comma_separator}}', knip(2, ',', '.'))
      .replace('{{amount_with_apostrophe_separator}}', knip(2, '.', "'"))
      .replace('{{amount_no_decimals}}', knip(0, '', ','))
      .replace('{{amount}}', knip(2, '.', ','));
  }
}

if (!customElements.get(TAG)) customElements.define(TAG, ZorenzaPrice);
